import {
  useCallback, useMemo, useRef, useState, type MouseEvent, type PointerEvent,
} from 'react';
import { isAreaFeature, type Coordinate, type EditableFloorPlan, type EditorFeature } from '../models/editor-feature';
import {
  featuresIn, nearestEdge, pickFeature, pickVertex, type EdgeHit, type VertexHit,
} from '../utils/hit-test';
import { elementToSurface, surfaceTransformOf } from '../utils/paint';
import { alignmentHandlesOf, lockToAxis, squareCornerAt } from '../utils/alignment';
import { multiPolygonCentroid } from '../utils/rotation';
import { boundsOf, handleAt, isResizable } from '../utils/selection-chrome';
import { editableFeaturesOf, selectionKeyOf } from '../utils/selection';
import { toScreen, toWorld, type Projection } from '../utils/projection';
import type { EditorDocument } from './use-editor-document';
import type { Selection } from './use-selection';
import type { Viewport } from './use-viewport';

/**
 * How a press on the selected shape's outline is read.
 *
 * - `off`: it is not; a press anywhere on the shape moves the whole thing.
 * - `corners`: a press on a corner drags that corner, and a press on a side adds a corner there.
 * - `sides`: a press on a side pushes or pulls that whole side, keeping it parallel — which is
 *   how a room is made bigger without anybody having to think about its corners.
 */
export type ReshapeMode = 'off' | 'corners' | 'sides';

/**
 * The cursor for pushing a side, from the side's direction on screen.
 *
 * A side running across the screen is pulled up or down, so it gets the vertical arrows; one
 * running up and down gets the horizontal ones. Anything in between gets the nearer diagonal.
 */
export const sideCursorOf = (from: { x: number; y: number }, to: { x: number; y: number }): string => {
  const degrees = ((Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI + 180) % 180;

  if (degrees < 22.5 || degrees >= 157.5) return 'ns-resize';
  if (degrees < 67.5) return 'nesw-resize';
  if (degrees < 112.5) return 'ew-resize';
  return 'nwse-resize';
};

export interface MarqueeRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface CanvasInteraction {
  readonly marquee: MarqueeRect | undefined;
  readonly dragging: boolean;
  readonly resizing: boolean;
  readonly rotating: boolean;
  readonly panning: boolean;
  /**
   * What the running transform has done so far — "120%" or "45°" — or undefined when no
   * transform is in progress. A resize with no number is guesswork the moment the admin
   * wants two shapes the same size.
   */
  readonly readout: string | undefined;
  /**
   * What the cursor should be for whatever is under the pointer right now — a grip's resize
   * arrows, or a pointer over a selectable feature.
   *
   * On the SVG renderer each element carried its own `cursor` style and the browser resolved
   * it. A canvas is one element with one cursor, so the answer has to be computed alongside
   * the hover test that is already running.
   */
  readonly hoverCursor: string | undefined;
  readonly onSurfacePointerDown: (event: PointerEvent<HTMLCanvasElement>) => void;
  readonly onSurfacePointerMove: (event: PointerEvent<HTMLCanvasElement>) => void;
  readonly onSurfacePointerUp: (event: PointerEvent<HTMLCanvasElement>) => void;
  /** A gesture the browser took away: ended like a release, but never read as a click. */
  readonly onSurfacePointerCancel: (event: PointerEvent<HTMLCanvasElement>) => void;
  readonly onSurfaceContextMenu: (event: MouseEvent<HTMLCanvasElement>) => void;
}

interface Props {
  readonly floorPlan: EditableFloorPlan | undefined;
  readonly projection: Projection;
  readonly viewport: Viewport;
  readonly selection: Selection;
  readonly document: EditorDocument;
  /** Set while a placement tool is armed; the next click on the surface places instead of selecting. */
  readonly onPlace?: (at: Coordinate) => void;
  /**
   * Set while a drawing or measuring tool is armed. Returns true when it consumed the click.
   *
   * Consulted before anything else a press could mean: while a polygon is being drawn, a
   * click that lands on an existing desk is still a vertex, not a selection.
   */
  readonly onDraw?: (at: Coordinate, straighten: boolean) => boolean;
  /** Reports the pointer to a drawing tool, so the segment being aimed follows the cursor. */
  readonly onDrawMove?: (at: Coordinate, straighten: boolean) => void;
  /** Reports which feature the pointer is over, so the surface can draw it highlighted. */
  readonly onHover?: (key: string | undefined) => void;
  /**
   * Where the pointer is, in the plan's own coordinates.
   *
   * What the status bar's readout is for. Every CAD tool has one, and on a plan whose units
   * are a project's own it is the only way to tell whether a drawing is in millimetres or in
   * metres without measuring something.
   */
  readonly onPointerAt?: (at: Coordinate | undefined) => void;
  /**
   * Editor ids of features the surface is not currently drawing.
   *
   * A hidden feature must not be pickable. When the browser decided what a click hit, a
   * feature that was not rendered simply was not there; hit-testing works off the document
   * instead, so it would happily select a retired desk out of an empty patch of floor.
   */
  readonly hiddenIds?: ReadonlySet<string>;
  /**
   * Whether the vertex tool is armed, in which case a press means the point under the cursor
   * rather than the shape it belongs to.
   *
   * The two cannot share a gesture: on the select tool a press inside a shape drags the whole
   * thing, and here the same press has to grab one corner of it.
   */
  readonly vertexMode?: boolean;
  /**
   * How a press on the selection's outline is read. Wins over [vertexMode] when given; without
   * it, the vertex tool means `corners` and everything else `off`.
   */
  readonly reshape?: ReshapeMode;
  /**
   * Which features a click, a hover or a marquee may reach. Everything, when absent.
   *
   * Set by an editing mode, so that with "Edit rooms" armed a click lands on a room even
   * when a desk or a wall is drawn on top of it.
   */
  readonly accept?: (feature: EditorFeature) => boolean;
  /**
   * Whether a press on a feature selects it without starting a drag.
   *
   * For the building's outline, which is reshaped by its corners and sides and never meant to
   * be dragged across the floor by a press that was aimed at one of them and missed.
   */
  readonly lockMove?: boolean;
  /**
   * Where a point lands once snapping has had its say, or absent when snapping is off.
   *
   * `excludeSelection` is set while the selection itself is being moved or reshaped, so it is
   * not pulled onto its own corners and left stuck where it started.
   */
  readonly snap?: (at: Coordinate, excludeSelection: boolean) => Coordinate;
  /**
   * Lines moving points up with what is already on the plan, returning the correction to add.
   *
   * On by default, for everything that moves: dragging a shape, a corner or a side, and the
   * points of something being drawn or placed. Absent, nothing is aligned.
   *
   * `exclude` names the features being moved, left out of what is lined up with so nothing
   * lines up with itself — named by the gesture rather than read from the selection, which
   * catches up a render after the press. `extraTargets` adds points of those features that
   * stay put and should still count — the other corners of a shape one of whose corners is
   * being dragged, which is what lets a dragged corner come level with its neighbour.
   * `lockX` / `lockY` hold an axis still.
   */
  readonly align?: (
    moving: readonly Coordinate[],
    options: {
      readonly exclude: readonly string[];
      readonly extraTargets?: readonly Coordinate[];
      readonly lockX?: boolean;
      readonly lockY?: boolean;
    },
  ) => { readonly dx: number; readonly dy: number };
  /**
   * Opens the context menu, with whatever was right-clicked.
   *
   * The feature is resolved here rather than in the component because this is where the
   * hit-tester already knows which features are hidden — and a menu offering to delete a
   * desk that is not being drawn is a menu acting on something the admin cannot see.
   */
  readonly onContextMenu?: (
    position: { x: number; y: number },
    feature: EditorFeature | undefined,
  ) => void;
}

/**
 * How far the pointer must travel before a press on empty surface counts as a drag rather
 * than a click. Without it, the smallest tremor while clicking to deselect would pan the
 * plan a pixel and swallow the click.
 */
const DRAG_THRESHOLD_PIXELS = 4;

/**
 * Where a pointer is in the surface's own units, which is what the projection speaks.
 *
 * Routed through the same transform the painter uses, rather than a second version of the
 * arithmetic. The two used to disagree whenever the element's aspect ratio differed from the
 * surface's: the drawing letterboxed and the pointer maths did not, so a click landed
 * further from the cursor the further it got from the centre.
 */
const toSurfacePoint = (
  event: { currentTarget: HTMLCanvasElement; clientX: number; clientY: number },
  viewBox: { x: number; y: number; width: number; height: number },
): { x: number; y: number } => {
  const bounds = event.currentTarget.getBoundingClientRect();

  return elementToSurface(
    { x: event.clientX - bounds.left, y: event.clientY - bounds.top },
    surfaceTransformOf(bounds.width, bounds.height, viewBox),
  );
};

/** Whether a key that straightens the segment being drawn is held: Shift, Ctrl/⌘ or Alt. */
const isStraightening = (event: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean; altKey: boolean }): boolean => (
  event.shiftKey || event.ctrlKey || event.metaKey || event.altKey
);

const screenPositionOf = (feature: EditorFeature, projection: Projection): { x: number; y: number } => {
  const anchor: Coordinate = isAreaFeature(feature)
    ? multiPolygonCentroid(feature.coordinates)
    : feature.coordinates;

  return toScreen(anchor, projection);
};

/**
 * Click, drag and box-select on the editor surface.
 *
 * The three gestures overlap on the same pointer, so the order they are resolved in matters:
 * a press on a feature starts a drag, a press with a modifier on empty surface starts a
 * marquee, and a plain press on empty surface clears — unless a marquee has just finished,
 * in which case its own release would otherwise read as that clearing click.
 */
export const useCanvasInteraction = ({
  floorPlan,
  projection,
  viewport,
  selection,
  document: editorDocument,
  onPlace,
  onDraw,
  onDrawMove,
  onHover,
  onPointerAt,
  hiddenIds,
  onContextMenu,
  vertexMode = false,
  reshape,
  accept,
  lockMove = false,
  snap,
  align,
}: Props): CanvasInteraction => {
  const { viewBox } = viewport;
  const reshapeMode: ReshapeMode = reshape ?? (vertexMode ? 'corners' : 'off');
  const pickOptions = useMemo(() => ({ hidden: hiddenIds, accept }), [hiddenIds, accept]);
  const [marquee, setMarquee] = useState<MarqueeRect | undefined>();
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [panning, setPanning] = useState(false);
  const [readout, setReadout] = useState<string | undefined>();
  const [hoverCursor, setHoverCursor] = useState<string | undefined>();
  // A press on empty surface is ambiguous until the pointer moves: it becomes a pan, or it
  // stays a click that clears the selection. This holds it open until one or the other.
  const pendingPan = useRef<{ clientX: number; clientY: number; moved: boolean } | undefined>();
  const marqueeOrigin = useRef<{ x: number; y: number } | undefined>();
  /** The point being dragged, while the vertex tool has hold of one. */
  const draggingVertex = useRef<VertexHit | undefined>();
  /** The side being pushed or pulled, and where the pointer was on the last move. */
  const draggingEdge = useRef<{ edge: EdgeHit; last: Coordinate } | undefined>();
  /**
   * A point of what is being dragged, and where the pointer was when the drag began.
   *
   * What makes a drag snappable: the pointer lands wherever the hand puts it, so it is the
   * grabbed corner — not the pointer — that is snapped, and the drag follows from there.
   */
  const dragAnchor = useRef<{ pointer: Coordinate; anchor: Coordinate; last: Coordinate } | undefined>();
  /**
   * A whole-selection drag, measured from where it started: the box handles it lines up by
   * and how far it has been moved so far. From the start rather than frame to frame, because
   * an alignment is a correction to the whole move — applied per frame it would compound.
   */
  const dragBox = useRef<{
    pointer: Coordinate;
    handles: Coordinate[];
    ids: string[];
    applied: Coordinate;
  } | undefined>();
  /**
   * A side being pushed, measured from where it started, for the same reason — with the rest of
   * its own shape, which stays put and is as good a line to push it level with as any other.
   */
  const edgeDrag = useRef<{
    pointer: Coordinate;
    ends: [Coordinate, Coordinate];
    ownPoints: Coordinate[];
    applied: Coordinate;
  } | undefined>();
  /**
   * An Alt-press on a corner, until the pointer moves. Released where it was pressed it removes
   * the corner, as Alt-click always has; moved, it becomes a drag with the corner squared up.
   */
  const pendingRemoval = useRef<{ clientX: number; clientY: number } | undefined>();

  /**
   * A point lined up with the plan, when alignment is on.
   *
   * Not on top of a snap: a snapped point already sits exactly on a corner or on the grid, and
   * lining it up afterwards could only pull it a few pixels off what it snapped to. The same
   * rule as moving a shape or a side, which align only while snapping is off.
   */
  const aligned = useCallback((at: Coordinate): Coordinate => {
    if (!align || snap) {
      return at;
    }

    const { dx, dy } = align([at], { exclude: [] });
    return [at[0] + dx, at[1] + dy];
  }, [align, snap]);

  const snapped = useCallback(
    (at: Coordinate, excludeSelection = false): Coordinate => (snap ? snap(at, excludeSelection) : at),
    [snap],
  );

  /** Moves an anchored drag on to where the pointer is now, returning how far it went. */
  const followAnchor = useCallback((world: Coordinate): Coordinate | undefined => {
    const drag = dragAnchor.current;

    if (!drag) {
      return undefined;
    }

    const target = snapped([
      drag.anchor[0] + (world[0] - drag.pointer[0]),
      drag.anchor[1] + (world[1] - drag.pointer[1]),
    ], true);
    const delta: Coordinate = [target[0] - drag.last[0], target[1] - drag.last[1]];

    dragAnchor.current = { ...drag, last: target };
    return delta;
  }, [snapped]);
  const lastWorldPoint = useRef<Coordinate | undefined>();
  // The distance from the selection's centre to the pointer when the resize began, plus the
  // centre itself. A drag is then a ratio of distances, which keeps the element under the
  // cursor no matter how far the pointer travels.
  const resizeOrigin = useRef<{ centre: { x: number; y: number }; distance: number } | undefined>();
  // The angle from the selection's centre to the pointer when the rotation began. Each move
  // applies the change since the last one, so the element tracks the cursor exactly.
  const rotateOrigin = useRef<{ centre: { x: number; y: number }; angle: number } | undefined>();
  // Totals for the readout. The gestures themselves apply their change step by step, so
  // neither of them knows how far it has come without this.
  const gestureTotal = useRef({ scale: 1, degrees: 0 });

  /**
   * A press that landed on a feature.
   *
   * Reached through hit-testing rather than through an element's own handler, but the
   * behaviour is unchanged: shift-clicking an already selected feature toggles it out, so a
   * drag would be meaningless; a plain click on an unselected one selects it first and then
   * drags.
   */
  const beginFeaturePress = useCallback(
    (feature: EditorFeature, event: PointerEvent<HTMLCanvasElement>, point: { x: number; y: number }) => {
      const additive = event.shiftKey;
      const alreadySelected = selection.keys.has(selectionKeyOf(feature));

      if (!alreadySelected || additive) {
        selection.select(feature, additive);
      }

      if (!additive && !lockMove) {
        const world = toWorld(point, projection);

        // The corner nearest the grab, for a shape: that is the point somebody lining a room
        // up against a wall is aiming with.
        const anchor: Coordinate = isAreaFeature(feature)
          ? feature.coordinates.flat(2).reduce((nearest, vertex) => (
            Math.hypot(vertex[0] - world[0], vertex[1] - world[1])
              < Math.hypot(nearest[0] - world[0], nearest[1] - world[1]) ? vertex : nearest
          ), feature.coordinates[0][0][0])
          : feature.coordinates;

        lastWorldPoint.current = world;
        dragAnchor.current = { pointer: world, anchor, last: anchor };

        // What is about to move: the pressed feature alone unless it was already part of the
        // selection. Read here, from the press, because the selection state catches up a render
        // later and the first frame of the drag would otherwise line up the wrong thing.
        const moving = alreadySelected ? selection.features : [feature];
        dragBox.current = {
          pointer: world,
          handles: alignmentHandlesOf(moving.flatMap((candidate) => (isAreaFeature(candidate)
            ? candidate.coordinates.flatMap((polygon) => polygon.flat())
            : [candidate.coordinates]))),
          ids: moving.map((candidate) => candidate.id),
          applied: [0, 0],
        };
        setDragging(true);
        event.currentTarget.setPointerCapture(event.pointerId);
      }
    },
    [selection, projection, lockMove],
  );

  /** Where the selection sits on screen, which is the pivot both transform gestures work from. */
  const selectionCentre = useCallback((): { x: number; y: number } | undefined => {
    if (!floorPlan) {
      return undefined;
    }

    const selected = editableFeaturesOf(floorPlan).filter((feature) => selection.keys.has(selectionKeyOf(feature)));
    if (!selected.length) {
      return undefined;
    }

    const positions = selected.map((feature) => screenPositionOf(feature, projection));

    return {
      x: positions.reduce((total, point) => total + point.x, 0) / positions.length,
      y: positions.reduce((total, point) => total + point.y, 0) / positions.length,
    };
  }, [floorPlan, selection.keys, projection]);

  const beginRotate = useCallback(
    (point: { x: number; y: number }) => {
      const centre = selectionCentre();
      if (!centre) {
        return;
      }

      gestureTotal.current = { scale: 1, degrees: 0 };
      setReadout('0°');
      rotateOrigin.current = {
        centre,
        // Screen angles grow clockwise, because the surface's Y axis points down — which is
        // also the direction a positive rotation turns an element, so no sign flip is needed.
        angle: Math.atan2(point.y - centre.y, point.x - centre.x),
      };
      setRotating(true);
    },
    [selectionCentre],
  );

  const beginResize = useCallback(
    (point: { x: number; y: number }) => {
      const centre = selectionCentre();
      if (!centre) {
        return;
      }

      const distance = Math.hypot(point.x - centre.x, point.y - centre.y);

      // A grab exactly on the centre would make every later ratio a division by zero.
      if (distance < 1) {
        return;
      }

      gestureTotal.current = { scale: 1, degrees: 0 };
      setReadout('100%');
      resizeOrigin.current = { centre, distance };
      setResizing(true);
    },
    [selectionCentre],
  );

  const onSurfacePointerDown = useCallback(
    (event: PointerEvent<HTMLCanvasElement>) => {
      if (event.button !== 0) {
        return;
      }

      const point = toSurfacePoint(event, viewBox);

      if (reshapeMode === 'sides' && floorPlan) {
        const edge = nearestEdge(point, selection.features.filter(isAreaFeature), projection);

        if (edge) {
          const world = toWorld(point, projection);
          const start = edge.feature.coordinates[edge.polygonIndex][edge.ringIndex][edge.afterVertex];

          event.currentTarget.setPointerCapture(event.pointerId);
          draggingEdge.current = { edge, last: world };
          dragAnchor.current = { pointer: world, anchor: start, last: start };
          const end = edge.feature.coordinates[edge.polygonIndex][edge.ringIndex][edge.afterVertex + 1];
          const isEnd = ([x, y]: Coordinate): boolean => (x === start[0] && y === start[1]) || (x === end[0] && y === end[1]);

          edgeDrag.current = {
            pointer: world,
            ends: [start, end],
            ownPoints: edge.feature.coordinates.flatMap((polygon) => polygon.flat()).filter((vertex) => !isEnd(vertex)),
            applied: [0, 0],
          };
          setDragging(true);
          return;
        }
      }

      if (reshapeMode === 'corners' && floorPlan) {
        // Only the selection's own points are editable. Every vertex on the plan would be
        // thousands of grabbable targets a pixel apart, and a press meant for a wall would
        // reshape whatever happened to be nearest.
        const editable = selection.features.filter(isAreaFeature);
        const vertex = pickVertex(point, editable, projection);

        if (vertex) {
          // Alt-click removes, as it always has; Alt-drag squares the corner up instead. Which of
          // the two it is is not known until the pointer either moves or lets go, so an Alt-press
          // is held open until then.
          pendingRemoval.current = event.altKey ? { clientX: event.clientX, clientY: event.clientY } : undefined;

          event.currentTarget.setPointerCapture(event.pointerId);
          draggingVertex.current = vertex;
          setDragging(true);
          return;
        }

        // Not on a point but on an edge: put one there and start dragging it, which is how
        // an outline gets extended. Pressing an edge and having nothing happen is the most
        // common way a points tool feels broken.
        const edge = nearestEdge(point, editable, projection);

        if (edge) {
          const world = toWorld(point, projection);

          pendingRemoval.current = undefined;

          editorDocument.insertVertexAt(
            edge.feature.id,
            edge.polygonIndex,
            edge.ringIndex,
            edge.afterVertex,
            world,
          );

          event.currentTarget.setPointerCapture(event.pointerId);
          draggingVertex.current = {
            feature: edge.feature,
            polygonIndex: edge.polygonIndex,
            ringIndex: edge.ringIndex,
            vertexIndex: edge.afterVertex + 1,
            at: world,
          };
          setDragging(true);
          return;
        }
      }

      // A drawing tool owns the click outright: mid-polygon, a press over an existing desk is
      // a vertex, not a selection.
      if (onDraw?.(aligned(snapped(toWorld(point, projection))), isStraightening(event))) {
        return;
      }

      // While a placement tool is armed the click belongs to the surface, even when it lands
      // on an existing feature — otherwise placing a desk on top of another one would
      // silently select that one instead.
      if (onPlace) {
        onPlace(aligned(snapped(toWorld(point, projection))));
        return;
      }

      // Grips first, then features, then empty surface. Same precedence the SVG renderer got
      // from painting order: the overlay was drawn last, so it caught the press first.
      //
      // No grips while reshaping: the surface does not draw them then, and a grip that works
      // without being shown turns a press meant for a corner into a resize.
      const bounds = reshapeMode === 'off' ? boundsOf(selection.features, projection) : undefined;
      const grip = bounds ? handleAt(point, bounds, isResizable(selection.features)) : undefined;

      if (grip) {
        event.currentTarget.setPointerCapture(event.pointerId);

        if (grip.kind === 'rotate') {
          beginRotate(point);
        } else {
          beginResize(point);
        }

        return;
      }

      const hit = floorPlan ? pickFeature(point, floorPlan, projection, pickOptions) : undefined;

      if (hit) {
        beginFeaturePress(hit, event, point);
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        marqueeOrigin.current = point;
        setMarquee({ x: point.x, y: point.y, width: 0, height: 0 });
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }

      // Dragging empty surface moves the plan, the same gesture the mobile app uses. Whether
      // this press is a drag or a click is not yet known, so nothing is decided here.
      pendingPan.current = { clientX: event.clientX, clientY: event.clientY, moved: false };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [
      viewBox,
      onPlace,
      onDraw,
      projection,
      selection.features,
      floorPlan,
      pickOptions,
      reshapeMode,
      editorDocument,
      beginRotate,
      beginResize,
      beginFeaturePress,
      snapped,
      aligned,
    ],
  );

  const onSurfacePointerMove = useCallback(
    (event: PointerEvent<HTMLCanvasElement>) => {
      // Reported before anything else claims the move, so the segment being aimed keeps
      // following the pointer even while the surface is deciding whether this is a pan.
      if (onDrawMove) {
        onDrawMove(aligned(snapped(toWorld(toSurfacePoint(event, viewBox), projection))), isStraightening(event));
      } else if (onPlace && (snap || align)) {
        // Nothing is drawn while a desk or label is being aimed, but the snap and the alignment
        // are still worked out, so their marker and guides show where the click will put it.
        aligned(snapped(toWorld(toSurfacePoint(event, viewBox), projection)));
      }

      if (pendingPan.current) {
        const origin = pendingPan.current;
        const travelled = Math.hypot(event.clientX - origin.clientX, event.clientY - origin.clientY);

        if (!origin.moved && travelled < DRAG_THRESHOLD_PIXELS) {
          return;
        }

        if (!origin.moved) {
          pendingPan.current = { ...origin, moved: true };
          setPanning(true);
        }

        const surface = event.currentTarget.getBoundingClientRect();
        // Divided by the drawing's own scale rather than by the element's width, so the plan
        // keeps up with the cursor exactly instead of sliding faster or slower than it
        // whenever the element is letterboxed.
        const { scale } = surfaceTransformOf(surface.width, surface.height, viewBox);

        viewport.panBy(
          -(event.clientX - origin.clientX) / scale,
          -(event.clientY - origin.clientY) / scale,
        );

        pendingPan.current = { clientX: event.clientX, clientY: event.clientY, moved: true };
        return;
      }

      if (rotateOrigin.current) {
        const point = toSurfacePoint(event, viewBox);
        const { centre, angle } = rotateOrigin.current;
        const nextAngle = Math.atan2(point.y - centre.y, point.x - centre.x);

        const degrees = ((nextAngle - angle) * 180) / Math.PI;
        if (Number.isFinite(degrees) && degrees !== 0) {
          editorDocument.rotateBy(selection.keys, degrees, 'rotate');
          rotateOrigin.current = { centre, angle: nextAngle };

          gestureTotal.current.degrees += degrees;
          setReadout(`${Math.round(gestureTotal.current.degrees)}°`);
        }

        return;
      }

      if (resizeOrigin.current) {
        const point = toSurfacePoint(event, viewBox);
        const { centre, distance } = resizeOrigin.current;
        const nextDistance = Math.hypot(point.x - centre.x, point.y - centre.y);

        // Applied as a step-by-step ratio rather than against the gesture's start, so the
        // scale composes with whatever the element already was.
        const factor = nextDistance / distance;
        if (Number.isFinite(factor) && factor > 0) {
          editorDocument.scaleBy(selection.keys, factor, 'resize');
          resizeOrigin.current = { centre, distance: nextDistance };

          gestureTotal.current.scale *= factor;
          setReadout(`${Math.round(gestureTotal.current.scale * 100)}%`);
        }

        return;
      }

      if (draggingEdge.current) {
        const { edge } = draggingEdge.current;
        const world = toWorld(toSurfacePoint(event, viewBox), projection);
        // Only the part square to the side is applied, and that is linear — so stepping the
        // side's first corner to each snapped target lands the side exactly on the last one.
        let delta = snap ? followAnchor(world) : undefined;

        const pushing = edgeDrag.current;

        if (!snap && pushing) {
          const [from, to] = pushing.ends;
          const length = Math.hypot(to[0] - from[0], to[1] - from[1]) || 1;
          const normal: Coordinate = [-(to[1] - from[1]) / length, (to[0] - from[0]) / length];
          const push = (total: Coordinate): number => (total[0] * normal[0]) + (total[1] * normal[1]);

          let total: Coordinate = [world[0] - pushing.pointer[0], world[1] - pushing.pointer[1]];

          // Lined up by where its two ends are about to be. The side only moves square to
          // itself, so the correction is kept to that direction too.
          if (align) {
            const along = push(total);
            const ends = [from, to].map(([x, y]): Coordinate => [x + (normal[0] * along), y + (normal[1] * along)]);
            const correction = align(ends, { exclude: [edge.feature.id], extraTargets: pushing.ownPoints });
            const correctionAlong = push([correction.dx, correction.dy]);
            total = [total[0] + (normal[0] * correctionAlong), total[1] + (normal[1] * correctionAlong)];
          }

          delta = [total[0] - pushing.applied[0], total[1] - pushing.applied[1]];
          edgeDrag.current = { ...pushing, applied: total };
        }

        if (delta && (delta[0] !== 0 || delta[1] !== 0)) {
          editorDocument.moveEdgeBy(
            edge.feature.id,
            edge.polygonIndex,
            edge.ringIndex,
            edge.afterVertex,
            delta[0],
            delta[1],
            'edge',
          );
        }

        draggingEdge.current = { edge, last: world };
        return;
      }

      if (draggingVertex.current) {
        const vertex = draggingVertex.current;

        if (pendingRemoval.current) {
          const origin = pendingRemoval.current;

          if (Math.hypot(event.clientX - origin.clientX, event.clientY - origin.clientY) < DRAG_THRESHOLD_PIXELS) {
            return;
          }

          pendingRemoval.current = undefined;
        }

        let target = snapped(toWorld(toSurfacePoint(event, viewBox), projection), true);

        // The ring as it is now, not as it was when the corner was grabbed: the drag has been
        // moving this very vertex, and its neighbours are what it squares up or lines up with.
        const live = floorPlan
          ? editableFeaturesOf(floorPlan).find((candidate) => candidate.id === vertex.feature.id)
          : undefined;
        const ring = live && isAreaFeature(live) ? live.coordinates[vertex.polygonIndex]?.[vertex.ringIndex] : undefined;

        if (ring && ring.length >= 4) {
          const closed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1];
          const corners = closed ? ring.slice(0, -1) : ring;
          const index = closed && vertex.vertexIndex === ring.length - 1 ? 0 : vertex.vertexIndex;
          // Every other point of the shape, this ring's other corners and any other ring's: the
          // shape itself is left out of the plan's targets, and these are the parts that stay put.
          const others = live && isAreaFeature(live)
            ? live.coordinates.flatMap((polygon, polygonIndex) => polygon.flatMap((points, ringIndex) => (
              polygonIndex === vertex.polygonIndex && ringIndex === vertex.ringIndex
                ? corners.filter((_, position) => position !== index)
                : points)))
            : [];

          if (event.altKey) {
            // Held Alt: the corner goes square with the corners either side of it.
            target = squareCornerAt(
              target,
              corners[(index - 1 + corners.length) % corners.length],
              corners[(index + 1) % corners.length],
            );
          } else if (align && !snap) {
            const { dx, dy } = align([target], { exclude: [vertex.feature.id], extraTargets: others });
            target = [target[0] + dx, target[1] + dy];
          }
        }

        editorDocument.moveVertexTo(
          vertex.feature.id,
          vertex.polygonIndex,
          vertex.ringIndex,
          vertex.vertexIndex,
          target,
          'vertex',
        );

        return;
      }

      if (marqueeOrigin.current) {
        const point = toSurfacePoint(event, viewBox);
        const origin = marqueeOrigin.current;

        setMarquee({
          x: Math.min(origin.x, point.x),
          y: Math.min(origin.y, point.y),
          width: Math.abs(point.x - origin.x),
          height: Math.abs(point.y - origin.y),
        });
        return;
      }

      if (!dragging || !lastWorldPoint.current) {
        // Nothing is being manipulated, so this move is a hover: report what is under the
        // pointer and what the cursor should say about it. A canvas has one cursor and no
        // per-element hover, so both answers have to be computed here.
        if (!dragging && floorPlan) {
          const point = toSurfacePoint(event, viewBox);
          onPointerAt?.(toWorld(point, projection));

          // While reshaping, the cursor says what a press on the outline would do: move a
          // corner, add one, or push the side — the difference between a reshape tool that is
          // discoverable and one that has to be explained.
          if (reshapeMode !== 'off') {
            const editable = selection.features.filter(isAreaFeature);

            if (reshapeMode === 'corners' && pickVertex(point, editable, projection)) {
              onHover?.(undefined);
              setHoverCursor('move');
              return;
            }

            const edge = nearestEdge(point, editable, projection);

            if (edge) {
              const ring = edge.feature.coordinates[edge.polygonIndex][edge.ringIndex];

              onHover?.(undefined);
              setHoverCursor(reshapeMode === 'sides'
                ? sideCursorOf(
                  toScreen(ring[edge.afterVertex], projection),
                  toScreen(ring[edge.afterVertex + 1], projection),
                )
                : 'copy');
              return;
            }
          }

          const bounds = reshapeMode === 'off' ? boundsOf(selection.features, projection) : undefined;
          const grip = bounds ? handleAt(point, bounds, isResizable(selection.features)) : undefined;

          if (grip) {
            onHover?.(undefined);
            setHoverCursor(grip.cursor);
            return;
          }

          const hovered = pickFeature(point, floorPlan, projection, pickOptions);
          onHover?.(hovered ? selectionKeyOf(hovered) : undefined);
          setHoverCursor(hovered ? 'pointer' : undefined);
        }

        return;
      }

      const world = toWorld(toSurfacePoint(event, viewBox), projection);

      // Deltas are taken against the previous pointer position rather than the gesture's
      // start, so the selection tracks the cursor exactly even as the plan is zoomed mid-drag.
      // With snapping on, the grabbed corner is what follows the pointer, and it is snapped.
      const anchored = snap ? followAnchor(world) : undefined;
      let deltaX = anchored ? anchored[0] : world[0] - lastWorldPoint.current[0];
      let deltaY = anchored ? anchored[1] : world[1] - lastWorldPoint.current[1];

      // Without snapping, the move is measured from where it began, so it can be held to one
      // axis (Alt) and lined up with the rest of the plan as a whole rather than frame by frame.
      const box = dragBox.current;

      if (!snap && box && (align || event.altKey)) {
        let totalX = world[0] - box.pointer[0];
        let totalY = world[1] - box.pointer[1];
        let lockX = false;
        let lockY = false;

        if (event.altKey) {
          ({
            dx: totalX, dy: totalY, lockX, lockY,
          } = lockToAxis(totalX, totalY));
        }

        if (align) {
          const correction = align(
            box.handles.map(([x, y]): Coordinate => [x + totalX, y + totalY]),
            { exclude: box.ids, lockX, lockY },
          );
          totalX += correction.dx;
          totalY += correction.dy;
        }

        deltaX = totalX - box.applied[0];
        deltaY = totalY - box.applied[1];
        dragBox.current = { ...box, applied: [totalX, totalY] };
      }

      // Floating-point drift on a zero move would still mark the document dirty and put an
      // entry in the history for it.
      if (deltaX !== 0 || deltaY !== 0) {
        editorDocument.move(selection.keys, deltaX, deltaY, 'drag');
      }

      lastWorldPoint.current = world;
    },
    [
      dragging,
      viewBox,
      projection,
      editorDocument,
      selection.keys,
      selection.features,
      viewport,
      floorPlan,
      pickOptions,
      reshapeMode,
      onHover,
      onPointerAt,
      onDrawMove,
      onPlace,
      snap,
      snapped,
      followAnchor,
      align,
      aligned,
    ],
  );

  const onSurfacePointerUp = useCallback(
    (event: PointerEvent<HTMLCanvasElement>) => {
      dragAnchor.current = undefined;
      dragBox.current = undefined;
      edgeDrag.current = undefined;

      if (draggingEdge.current) {
        draggingEdge.current = undefined;
        setDragging(false);
        editorDocument.endGesture();
        return;
      }

      if (draggingVertex.current) {
        const vertex = draggingVertex.current;
        draggingVertex.current = undefined;
        setDragging(false);

        // An Alt-press that never moved: the click that removes a corner.
        if (pendingRemoval.current) {
          pendingRemoval.current = undefined;
          editorDocument.removeVertexAt(vertex.feature.id, vertex.polygonIndex, vertex.ringIndex, vertex.vertexIndex);
          return;
        }

        editorDocument.endGesture();
        return;
      }

      if (rotateOrigin.current) {
        rotateOrigin.current = undefined;
        setRotating(false);
        setReadout(undefined);
        editorDocument.endGesture();
        return;
      }

      if (resizeOrigin.current) {
        resizeOrigin.current = undefined;
        setResizing(false);
        setReadout(undefined);
        editorDocument.endGesture();
        return;
      }

      if (marqueeOrigin.current) {
        const rect = marquee;
        marqueeOrigin.current = undefined;
        setMarquee(undefined);

        if (rect && floorPlan) {
          // Through the shared hit-test, so a marquee catches an area by any of its vertices
          // rather than only by its centre — dragging a box over part of a long wall is the
          // gesture people actually make.
          selection.selectKeys(
            featuresIn(rect, floorPlan, projection, pickOptions).map(selectionKeyOf),
            event.shiftKey,
          );
        }

        // This release will also arrive as a click on empty surface; without the flag it
        // would clear the selection the marquee just made.
        selection.suppressNextClear();
        return;
      }

      if (pendingPan.current) {
        const { moved } = pendingPan.current;
        pendingPan.current = undefined;
        setPanning(false);

        // A press that never became a drag is a click on empty surface, which clears the
        // selection — unless a marquee just ended, whose own release arrives here too.
        if (!moved && !selection.consumeClearSuppression()) {
          selection.clear();
        }

        return;
      }

      if (dragging) {
        setDragging(false);
        lastWorldPoint.current = undefined;
        editorDocument.endGesture();
      }
    },
    [marquee, floorPlan, projection, selection, dragging, editorDocument, pickOptions],
  );

  // A cancelled Alt-press on a corner is not the click that removes it: a touch the browser
  // took over, or a pen lifted out of range, would otherwise delete a corner nobody let go of.
  const onSurfacePointerCancel = useCallback(
    (event: PointerEvent<HTMLCanvasElement>) => {
      pendingRemoval.current = undefined;
      onSurfacePointerUp(event);
    },
    [onSurfacePointerUp],
  );

  const onSurfaceContextMenu = useCallback(
    (event: MouseEvent<HTMLCanvasElement>) => {
      if (!onContextMenu) {
        return;
      }

      // On a Mac, Ctrl + click is a right-click. While drawing or placing, the click has already
      // put a point or a desk down, and must not also open a menu over the plan.
      if ((onDraw || onPlace) && event.ctrlKey) {
        event.preventDefault();
        return;
      }

      // The browser's own menu has nothing to offer on a drawing surface, and it covers the
      // one that does.
      event.preventDefault();

      const point = toSurfacePoint(event, viewBox);
      const hit = floorPlan ? pickFeature(point, floorPlan, projection, pickOptions) : undefined;

      // Right-clicking something outside the selection selects it, exactly as it does in
      // every drawing tool: the alternative is a menu whose actions apply to something other
      // than what was clicked. Right-clicking *inside* a selection leaves it alone, which is
      // what makes "select forty desks, right-click, align" possible at all.
      if (hit && !selection.isSelected(hit)) {
        selection.select(hit);
      }

      onContextMenu({ x: event.clientX, y: event.clientY }, hit);
    },
    [onContextMenu, onDraw, onPlace, viewBox, floorPlan, projection, pickOptions, selection],
  );

  return {
    marquee,
    dragging,
    resizing,
    rotating,
    panning,
    readout,
    hoverCursor,
    onSurfacePointerDown,
    onSurfacePointerMove,
    onSurfacePointerUp,
    onSurfacePointerCancel,
    onSurfaceContextMenu,
  };
};

export default useCanvasInteraction;
